import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Download, Printer } from '@phosphor-icons/react';
import { useCars } from '../context/CarsContext';
import { config } from '../config/runtimeConfig';
import CarPrintTemplate from '../components/CarPrintTemplate';
import PrintControls from '../components/print/PrintControls';
import './CarPrintPage.css';

const SUPPORTED_LANGUAGES = ['fr', 'en', 'de', 'lu'];
const DEFAULT_LANGUAGE = 'fr';

/**
 * Берём `navigator.language` (например `de-DE`), отсекаем регион, проверяем
 * что код в whitelist. Если нет — fallback на 'fr' (Autodis Luxembourg).
 */
const detectDefaultLanguage = () => {
  if (typeof navigator === 'undefined') return DEFAULT_LANGUAGE;
  const raw = navigator.language || navigator.languages?.[0] || '';
  const code = raw.toLowerCase().split('-')[0];
  return SUPPORTED_LANGUAGES.includes(code) ? code : DEFAULT_LANGUAGE;
};

const CarPrintPage = () => {
  const { id } = useParams();
  // Duty-free / pricing / QR-генерация инкапсулированы в CarPrintTemplate —
  // на этом уровне нужен только id-resolver и loading-state. cars/isLoaded
  // используются в diagnostic-логе ниже (см. ветку Car not found).
  const { getCarById, loading, isLoaded, cars } = useCars();

  const car = getCarById(id);

  // used-VIN (Andys и пр.) — НЕ new-model (Autodis). Листовка landscape-only:
  // прячем Orientation, форсим horizontal (кроме qr/catalog-qr — вертикальный
  // sticker), не инвертируем по умолчанию, нейтральный хром панели. Autodis
  // (new-model) этим не затрагивается. (#1064)
  const isUsedVin = !!car && !car.isNewModel;

  // Controls state — собирается из всех опций PrintControls sidebar (#1021).
  // Логика mirror/white/orientation/language применяется к template:
  //  - mirror: CSS transform: scaleX(-1) на template-wrapping div
  //  - white/orientation/language пока в state, реализация в template
  //    добавится отдельной итерацией (см. issue #1021 / #1024).
  // Mode default — поддерживает `?mode=catalog-qr` URL-параметр от b2b-ui
  // Inventory toolbar (#1022). Whitelist чтобы случайный мусор в URL не
  // вышибал шаблон.
  const initialModeFromUrl = useMemo(() => {
    const fromUrl = new URLSearchParams(window.location.hash.split('?')[1] || window.location.search).get('mode');
    return ['full', 'qr-only', 'catalog-qr'].includes(fromUrl) ? fromUrl : null;
  }, []);
  const [mode, setMode] = useState(initialModeFromUrl || 'full');
  // Lock Type toggle когда страница открыта с `?mode=catalog-qr` через
  // Inventory toolbar — оператор уже принял решение «catalog QR, не per-car»,
  // переключение обратно на Car info нелогично (никакая машина не выбрана,
  // seed-машина для catalog-qr — артефакт URL'а).
  const lockType = initialModeFromUrl === 'catalog-qr';
  const [format, setFormat] = useState('pdf'); // 'pdf' | 'svg'
  const [orientation, setOrientation] = useState('horizontal'); // 'horizontal' | 'vertical'
  const [mirror, setMirror] = useState('off'); // 'off' | 'on'
  const [color, setColor] = useState('black'); // 'black' | 'white'
  // Default language — берём из браузера, fallback на `fr` (Autodis Luxembourg)
  // если язык браузера не в нашем whitelist (см. issue #1024).
  const [language, setLanguage] = useState(() => detectDefaultLanguage());
  const [cutMarks, setCutMarks] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const templateRef = useRef(null);

  const handleAction = useCallback(async () => {
    if (!car || downloading) return;
    setDownloading(true);
    try {
      // Lazy-load exporter chunk — opentype.js + jspdf + svg2pdf и шрифты
      // тянутся только сейчас, в main bundle их нет (issue #949).
      const { buildSvg, downloadSvg, downloadPdf, buildFilename } = await import(
        '../components/print/exporters/templateExporter'
      );
      const templateEl = templateRef.current?.querySelector('.print-template');
      if (!templateEl) throw new Error('Template DOM not found');
      const svgText = await buildSvg(templateEl, car, mode);
      const filename = buildFilename(car, mode, format);
      if (format === 'svg') {
        downloadSvg(svgText, filename);
      } else {
        await downloadPdf(svgText, filename);
      }
    } catch (err) {
      console.error('[CarPrintPage] export failed:', err);
      alert(`Failed to generate ${format.toUpperCase()}: ${err?.message || err}`);
    } finally {
      setDownloading(false);
    }
  }, [car, downloading, format, mode]);

  // Auto-print по `?autoprint=true` отключён (issue #949): пользователь
  // сам выбирает action — Print, Download PDF, Download SVG, режим Full/QR.
  // Системный диалог печати вызывается явным кликом на кнопку Print.

  // qr/catalog-qr — вертикальный sticker; для used-VIN full-режим форсим
  // landscape (вертикальной версии листовки нет). (#1064)
  const isQrMode = mode === 'qr-only' || mode === 'catalog-qr';
  const effectiveOrientation = isUsedVin && !isQrMode ? 'horizontal' : orientation;
  // used-VIN (Andys) — мультиязычности нет, форсим English ('Powered by',
  // а не 'Propulsé par'). Language control для used-VIN и так скрыт. Autodis
  // (new-model) сохраняет выбранный/браузерный язык. (#1064)
  const effectiveLanguage = isUsedVin ? 'en' : language;

  // used-VIN: дефолт Color = 'white' (не инвертировано). Срабатывает один раз
  // при определении used-VIN; оператор потом может выбрать инверсию вручную.
  useEffect(() => {
    if (isUsedVin) setColor('white');
  }, [isUsedVin]);

  // Inject A4 @page rule (landscape | portrait) в зависимости от orientation
  // (#1021). CSS-only решение (named page qrtemplate) перебивалось дефолтным
  // @page { size: A4 } из глобального print.css (issue #944). Тут мы
  // добавляем style ПОСЛЕ всего — выигрывает кратчайший путь cascade-а.
  // Removable на unmount чтобы не ломать print других страниц.
  useEffect(() => {
    const style = document.createElement('style');
    style.dataset.printOrientation = 'qrtemplate';
    const pageSize = effectiveOrientation === 'vertical' ? 'A4 portrait' : 'A4 landscape';
    style.textContent = `@page { size: ${pageSize}; margin: 0; }`;
    document.head.appendChild(style);
    return () => { style.remove(); };
  }, [effectiveOrientation]);

  // Color и Format больше не связаны — оба варианта (white/black) можно
  // скачать как PDF и как SVG. Раньше color=black форсил SVG из-за того
  // что svg2pdf/jspdf конвертация не сохраняла CSS `filter: invert(1)`;
  // если эта проблема всё ещё актуальна — решается на уровне exporter'а,
  // не через UX лок.

  // QR-only / catalog-qr mode рендерятся только в portrait (одна
  // центрированная панель под крепление к стеклу — landscape для одного
  // QR не имеет смысла). PrintControls прячет Orientation toggle.
  // priorOrientationRef хранит выбор оператора до входа в qr-режим.
  // Capture-у ref'а делаем БЕЗУСЛОВНО при входе (даже если orientation
  // уже == 'vertical'): иначе full+vertical → qr-only → full ловит
  // initial ref-значение ('horizontal') и сбрасывает выбор юзера.
  const priorOrientationRef = useRef(orientation);
  useEffect(() => {
    const isQrMode = mode === 'qr-only' || mode === 'catalog-qr';
    if (isQrMode) {
      priorOrientationRef.current = orientation;
      if (orientation !== 'vertical') {
        setOrientation('vertical');
      }
    } else if (priorOrientationRef.current !== orientation) {
      setOrientation(priorOrientationRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Пока инвентори ещё грузится — показываем Loading, не «Car not found».
  // CarsContext выставляет loading=false ТОЛЬКО после resolved/rejected fetch,
  // но isLoaded становится true только при успехе. До успешной загрузки
  // считаем что car может ещё появиться.
  if (loading || !isLoaded) {
    return <div className="print-template__loading">Loading…</div>;
  }

  if (!car) {
    // Инвентори загрузилось, но машины с этим id в нём нет. Чаще всего
    // это значит что b2b-demo получил инвентори ДРУГОЙ организации — например
    // showroomTokenResolver не смог достучаться до b2b-api и упал на defaults
    // (org=1, slug=demo-autopolis). Логируем диагностику чтобы было видно
    // в DevTools console при сообщении бага.
    console.warn('[CarPrintPage] Car not found for id:', id, {
      requestedId: id,
      inventorySize: cars.length,
      sampleIds: cars.slice(0, 5).map((c) => c.id),
      apiUrl: config.CAR_API_URL,
      organizationId: config.ORGANIZATION_ID,
      inventorySlug: config.INVENTORY_SLUG,
    });
    return (
      <div className="print-template__loading print-template__loading--error">
        <div>Car not found</div>
        <div className="print-template__loading-detail">
          ID: {id} · Inventory size: {cars.length}
        </div>
      </div>
    );
  }

  const actionLabel = downloading ? 'Generating…' : `Download ${format.toUpperCase()}`;

  // Mirror через CSS transform: scaleX(-1) на template-wrapper div.
  // Класс read-ится в @media print тоже — печатается mirrored. Сканеру QR
  // (если он есть в template) error correction уровня 'H' справится с
  // зеркальностью только частично, поэтому Mirror предназначен ТОЛЬКО для
  // наклеек, которые клеятся изнутри стекла (через стекло смотрящий снаружи
  // видит нормально).
  const templateClasses = ['print-template-render'];
  if (mirror === 'on') templateClasses.push('print-template-render--mirror');
  // color === 'black' = swatch с белой буквой на чёрном фоне →
  // template такой же (чёрный фон, белый контент). 'white' = default
  // light template (без модификатора).
  if (color === 'black') templateClasses.push('print-template-render--inverted');
  if (cutMarks) templateClasses.push('print-template-render--cut-marks');
  const templateClass = templateClasses.join(' ');

  return (
    <div className={`print-template-wrapper${isUsedVin ? ' print-template-wrapper--neutral-chrome' : ''}`}>
      <div className={templateClass} ref={templateRef}>
        <CarPrintTemplate car={car} mode={mode} orientation={effectiveOrientation} language={effectiveLanguage} />
      </div>

      <PrintControls
        language={language}
        onLanguageChange={setLanguage}
        showLanguage={!!car?.isNewModel}
        mode={mode}
        onModeChange={setMode}
        showMode={!isUsedVin && !lockType}
        orientation={effectiveOrientation}
        onOrientationChange={setOrientation}
        showOrientation={!isUsedVin}
        showCatalogQr={!isUsedVin}
        showMirror={!isUsedVin}
        mirror={mirror}
        onMirrorChange={setMirror}
        showColor={!isUsedVin}
        color={color}
        onColorChange={setColor}
        format={format}
        onFormatChange={setFormat}
        cutMarks={cutMarks}
        onCutMarksChange={setCutMarks}
      >
        <button
          type="button"
          className="print-template__action-btn print-template__action-btn--compact"
          onClick={handleAction}
          disabled={downloading}
        >
          <Download className="print-template__action-btn-icon" weight="regular" />
          {actionLabel}
        </button>
        <button
          type="button"
          className="print-template__action-btn print-template__action-btn--compact"
          onClick={() => window.print()}
          disabled={downloading}
        >
          <Printer className="print-template__action-btn-icon" weight="regular" />
          Print
        </button>
      </PrintControls>
    </div>
  );
};

export default CarPrintPage;
